import * as React from "react";
import { Button, type ButtonProps, Typography } from "@mui/material";

type Primary = {
  kind: "primary";
  maxWidth?: "228px" | "fit-content";
};

type Secondary = {
  kind: "secondary";
  maxWidth?: "228px" | "fit-content";
};

type ModalPrimary = {
  kind: "modal-primary";
};

type Disabled = {
  kind: "disabled";
  maxWidth?: "228px" | "fit-content";
};

type HCButtonProps = {
  state: Primary | Secondary | ModalPrimary | Disabled;
  children: React.ReactNode;
};

export const HCButton = React.forwardRef<HTMLButtonElement, ButtonProps & HCButtonProps>(
  (props, ref) => {
    const { state, children, ...restProps } = props;
    switch (state.kind) {
      case "primary":
        return (
          <Button
            ref={ref}
            type="button"
            disableRipple
            disableFocusRipple
            {...restProps}
            variant="contained"
            sx={{
              color: "var(--color-neutral-100)",
              background: "linear-gradient(180deg, #DD3162 0%, #CE043B 33.17%, #C60339 100%)",
              width: "100%",
              maxWidth: state.maxWidth ?? "100%",
              height: "52px",
              angle: "0 deg",
              borderRadius: "40px",
              borderWidth: "1px",
              gap: "4p",
              paddingY: "8px",
              paddingX: "24px",
              boxShadow: "0px 3px 6px 0px hsla(249, 87%, 28%, 0.14)",
            }}
          >
            <Typography
              variant="button"
              sx={{
                textTransform: "lowercase",
                "&::first-letter": {
                  textTransform: "capitalize",
                },
              }}
            >
              {children}
            </Typography>
          </Button>
        );
      case "secondary":
        return (
          <Button
            ref={ref}
            type="button"
            disableRipple
            disableFocusRipple
            {...restProps}
            sx={{
              background: "transparent",
              color: "var(--color-brand-primary-500)",
              height: "52px",
              paddingY: "8px",
              paddingX: "24px",
              borderRadius: "40px",
              width: "100%",
              maxWidth: state.maxWidth ?? "100%",
            }}
          >
            <Typography
              variant="button"
              sx={{
                textTransform: "lowercase",
                "&::first-letter": {
                  textTransform: "capitalize",
                },
              }}
            >
              {children}
            </Typography>
          </Button>
        );
      case "modal-primary":
        return (
          <Button
            ref={ref}
            type="button"
            disableRipple
            disableFocusRipple
            {...restProps}
            variant="contained"
            sx={{
              color: "var(--color-neutral-100)",
              background: "linear-gradient(180deg, #DD3162 0%, #CE043B 33.17%, #C60339 100%)",
              width: "157px",
              height: "52px",
              angle: "0 deg",
              borderRadius: "40px",
              borderWidth: "1px",
              gap: "4p",
              paddingY: "8px",
              paddingX: "24px",
            }}
          >
            <Typography
              variant="button"
              sx={{
                textTransform: "lowercase",
                "&::first-letter": {
                  textTransform: "capitalize",
                },
              }}
            >
              {children}
            </Typography>
          </Button>
        );
      case "disabled":
        return (
          <Button
            ref={ref}
            type="button"
            disableRipple
            disableFocusRipple
            {...restProps}
            variant="contained"
            disabled
            sx={{
              "&.Mui-disabled": {
                color: "var(--color-neutral-100)",
                background: "hsla(213, 93%, 17%, 0.2)",
                width: "100%",
                maxWidth: state.maxWidth ?? "100%",
                height: "52px",
                angle: "0 deg",
                border: "1px solid hsla(0, 0%, 100%, 0.3)",
                borderRadius: "40px",
                gap: "4p",
                paddingY: "8px",
                paddingX: "24px",
              },
            }}
          >
            <Typography
              variant="button"
              sx={{
                textTransform: "lowercase",
                "&::first-letter": {
                  textTransform: "capitalize",
                },
              }}
            >
              {children}
            </Typography>
          </Button>
        );
    }
  },
);

HCButton.displayName = "HCButton";
